import React from "react";
import TaxCard from "./TaxCard";
import { css } from "emotion";
import { Highlight } from "../../search/Highlight";
import { getRequest } from "../../../helpers/ApiHelpers";
import { convertToFileDownload } from "../../../helpers/ConvertToFileDownload";
import { getAnswer } from "../../../helpers/GetAnswer";
import DOMPurify from "dompurify";

const sanitizeNote = (note) => {
  if (!note) return "";
  const html = /<\/?[a-z][\s\S]*>/i.test(note)
    ? note
    : `<p>${note.replace(/\n/g, "<br>")}</p>`;
  return DOMPurify.sanitize(html);
};

const paragraphStyles = css({
  color: "#000",
  fontSize: 14,
  lineHeight: 1.5,
  whiteSpace: "pre-wrap"
});
const labelStyles = css({
  fontFamily: "Fakt-Bold",
  fontSize: 14,
  color: "#707898",
  marginBottom: 14,
  marginTop: 24
});

const hasContent = (string) => {
  if (!string) return false;
  const text = string.replace(/<[^>]*>/g, "").replace(/\s/g, "");
  return text.length > 0;
};

const checkIfEmpty = (bool, string) => {
  return bool === true || bool === false || hasContent(string);
};


async function download(id) {
  const result = await getRequest(`/tax/downloadTransferTaxFile?id=${id}`);
  if (result.success) {
    convertToFileDownload(result.data.base64File, result.data.name);
  }
}

function TransferTax({ tax, editRoute }) {
  return (
    <TaxCard
      title="Transfer Tax"
      isApplicable={tax.isApplicable}
      editRoute={editRoute}
    >
      {hasContent(tax.isApplicableNote) && (
        <div
          className={`ql-editor ${css({ "& p": { margin: "0 !important" } })}`}
          dangerouslySetInnerHTML={{ __html: sanitizeNote(tax.isApplicableNote) }}
        />
      )}
      {checkIfEmpty(tax.isApplicableToLease, tax.isApplicableToLeaseNote) && (
        <React.Fragment>
          <div className={labelStyles}>Is it applicable to Leases?</div>
          <p className={paragraphStyles}>{getAnswer(tax.isApplicableToLease)}</p>
          {hasContent(tax.isApplicableToLeaseNote) && (
            <div
              className={`ql-editor ${css({ "& p": { margin: "0 !important" } })}`}
              dangerouslySetInnerHTML={{ __html: sanitizeNote(tax.isApplicableToLeaseNote) }}
            />
          )}
        </React.Fragment>
      )}
      {checkIfEmpty(
        tax.isApplicableToLeaseAssignment,
        tax.isApplicableToLeaseAssignmentNote
      ) && (
        <React.Fragment>
          <div className={labelStyles}>
            Is it applicable to Assignments of Leases?
          </div>
          <p className={paragraphStyles}>{getAnswer(tax.isApplicableToLeaseAssignment)}</p>
          {hasContent(tax.isApplicableToLeaseAssignmentNote) && (
            <div
              className={`ql-editor ${css({ "& p": { margin: "0 !important" } })}`}
              dangerouslySetInnerHTML={{ __html: sanitizeNote(tax.isApplicableToLeaseAssignmentNote) }}
            />
          )}
        </React.Fragment>
      )}
      {checkIfEmpty(
        tax.isApplicableToMembershipInterestTransfers,
        tax.isApplicableToMembershipInterestTransfersNote
      ) && (
        <React.Fragment>
          <div className={labelStyles}>
            Is it applicable to Membership Interest Transfers?
          </div>
          <p className={paragraphStyles}>{getAnswer(tax.isApplicableToMembershipInterestTransfers)}</p>
          {hasContent(tax.isApplicableToMembershipInterestTransfersNote) && (
            <div
              className={`ql-editor ${css({ "& p": { margin: "0 !important" } })}`}
              dangerouslySetInnerHTML={{ __html: sanitizeNote(tax.isApplicableToMembershipInterestTransfersNote) }}
            />
          )}
        </React.Fragment>
      )}
      {checkIfEmpty(
        tax.isExemptOfPersonalPropertyTransfer,
        tax.isExemptOfPersonalPropertyTransferNote
      ) && (
        <React.Fragment>
          <div className={labelStyles}>
            Is the transfer of personal property exempt?
          </div>
          <p className={paragraphStyles}>{getAnswer(tax.isExemptOfPersonalPropertyTransfer)}</p>
          {hasContent(tax.isExemptOfPersonalPropertyTransferNote) && (
            <div
              className={`ql-editor ${css({ "& p": { margin: "0 !important" } })}`}
              dangerouslySetInnerHTML={{ __html: sanitizeNote(tax.isExemptOfPersonalPropertyTransferNote) }}
            />
          )}
        </React.Fragment>
      )}
      {tax.transferTaxFiles && tax.transferTaxFiles.length > 0 && (
        <div className={labelStyles}>Attachments</div>
      )}
      {tax.transferTaxFiles &&
        tax.transferTaxFiles.map(f => (
          <p className={paragraphStyles} key={f.id}>
            <a
              onClick={() => download(f.id)}
              className={css({
                "&:hover": { textDecoration: "underline", color: "#00b8d4" }
              })}
            >
              <i
                className="fal fa-paperclip"
                style={{ color: "#00b8d4", marginRight: 8 }}
              />
              <Highlight children={f.fileName} />
            </a>
          </p>
        ))}
    </TaxCard>
  );
}

export default React.memo(TransferTax);
